"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import apiClient from "@/lib/api";
import { splitTtsChunks } from "@/lib/interview/ttsChunks";
import { DEFAULT_INTERVIEWER_KEY, resolveInterviewer } from "@/lib/interviewers";

const MEDIA_HEADERS = { "x-app-request": "1" };

// 読み上げ生成の待ち時間の上限。生成中は開始ボタンを押せないため、外部サービスが
// 詰まったときに操作不能のまま待たせないよう、バックエンドの読み取り上限（60秒）
// より手前で諦めて案内へ倒す。文単位のチャンクで生成するときはチャンクごとに適用する（#1966）。
const GENERATE_TTS_TIMEOUT_MS = 20000;

// 再生する質問のチャンクを同時に生成する本数。ElevenLabs はプランごとに同時接続数の上限が
// あるため、1 セッションあたりは「再生中の次」と「その次」までに抑える（#1966）。
const PLAYBACK_CONCURRENCY = 2;
// 次の質問の先読みは急がないため 1 本ずつ生成し、再生中の質問の生成が終わるまでは要求を出さない。
const PREFETCH_CONCURRENCY = 1;

/**
 * チャンクの再生待ち列（#1966）。面接官アバターが届いた順に再生し、列が尽きても
 * complete でなければ「話している」状態のまま次のチャンクを待つ。
 */
export interface TtsAudioQueue {
  /** 準備の要求ごとの識別子。変わったら先頭から再生し直す */
  id: number;
  /** 届いた順のチャンクの Blob URL */
  urls: string[];
  /** 全チャンクが揃った（または途中で打ち切った）とき true */
  complete: boolean;
}

// チャンクの生成ジョブ。先読みと再生の両方が同じ形で持ち、再生時に先読み済みのジョブを引き継ぐ。
interface ChunkJob {
  chunks: string[];
  urls: string[];
  complete: boolean;
  // 失敗したチャンクの番号。null なら失敗していない
  failedAt: number | null;
  cancelled: boolean;
  listeners: Set<() => void>;
  // 同時に先行して要求する本数。先読みを再生に引き継いだら増やす
  concurrency: number;
  // 新しい要求を出してよいか。先読みは再生中の生成と重ならないよう、再生の生成が終わるまで待つ
  canStart: () => boolean;
  // 進行中の要求をまとめて中断する。捨てた先読み・離脱後の生成を外部サービス側で続けない
  controller: AbortController;
  // 出せる要求を出す。concurrency・canStart を変えたとき、取り消したときに呼ぶ
  topUp: () => void;
}

interface UsePrepareTtsOptions {
  sessionId?: string;
  /**
   * 面接官の識別子（#1863）。質問別映像と事前生成音声を面接官のディレクトリで探す。
   * 省略・未知の値は現行の女性面接官。
   */
  interviewerKey?: string | null;
}

interface PrepareTtsOptions {
  autoPlay?: boolean;
  /**
   * 読み上げを生成する API の経路を差し替える。大学が登録した質問（#1861）は共通質問の
   * ID を持たないため、学生向けの経路（/university_link/questions/:id/tts）を渡す。
   * 指定があれば、セッション・質問 ID からの経路の組み立てより優先する。
   */
  generatePath?: string;
}

interface UsePrepareTtsReturn {
  audioSrc: string | null;
  /**
   * 文単位のチャンクで生成したときの再生待ち列（#1966）。セッション経由の生成でだけ使い、
   * このとき audioSrc は null のまま。静的ファイル・全文 1 回の生成では null。
   */
  audioQueue: TtsAudioQueue | null;
  customVideoSrc: string | null;
  ttsReady: boolean;
  ttsLoading: boolean;
  ttsPrepared: boolean;
  // 読み上げを用意できなかったことを呼び出し側へ伝える。true でも ttsPrepared /
  // ttsReady は true になり、テキストのみで練習を続けられる。
  ttsFailed: boolean;
  prepareTts: (text: string, questionId?: number | null, options?: PrepareTtsOptions) => Promise<void>;
  /**
   * 次の質問の読み上げを先読みする（#1966）。セッション経由の生成でだけ動き、質問 ID を持つ
   * 固定の設問（事前生成音声がある）は生成しない。1 問先までを保持し、古い先読みは捨てる。
   */
  prefetchTts: (text: string, questionId?: number | null) => void;
  playTts: () => void;
  resetTts: () => void;
  markTtsReady: () => void;
}

function createChunkJob(chunks: string[], concurrency: number, canStart: () => boolean = () => true): ChunkJob {
  return {
    chunks,
    urls: [],
    complete: false,
    failedAt: null,
    cancelled: false,
    listeners: new Set(),
    concurrency,
    canStart,
    controller: new AbortController(),
    topUp: () => {},
  };
}

function notifyJob(job: ChunkJob) {
  job.listeners.forEach((listener) => listener());
}

// ジョブを止め、進行中の要求を中断し、作った Blob URL を解放する。以後に届くチャンクは捨てる。
// 1 本目の到着を待っている呼び出しが残らないよう、止めたことを通知してから購読を外す。
function cancelChunkJob(job: ChunkJob) {
  job.cancelled = true;
  job.controller.abort();
  job.urls.forEach((url) => URL.revokeObjectURL(url));
  job.urls = [];
  job.topUp();
  notifyJob(job);
  job.listeners.clear();
}

// チャンクを順に生成して job.urls に積む。job.concurrency 本まで先行して要求し、届いた順を守る。
// 要求本文は全文 1 回の生成と同じ text だけにする。eleven_v3 は前後の文脈（previous_text /
// next_text）に未対応で、付けると 400 になる（#1966）。
async function runChunkJob(job: ChunkJob, generatePath: string) {
  const { chunks } = job;
  const requests: Array<Promise<Blob> | undefined> = [];
  // 今待っているチャンクの番号
  let cursor = 0;
  // 要求を出せない間の待ちを解く
  let wake: (() => void) | null = null;

  const start = (index: number) => {
    if (index >= chunks.length || requests[index] || job.cancelled) return;
    const request = apiClient
      .post(
        generatePath,
        { text: chunks[index] },
        { responseType: "blob", timeout: GENERATE_TTS_TIMEOUT_MS, signal: job.controller.signal },
      )
      .then((res) => res.data as Blob);
    // 失敗・取り消しで先に抜けた後に届く拒否を未処理のまま残さない。待つ側は request 自体で受け取る
    request.catch(() => {});
    requests[index] = request;
  };

  job.topUp = () => {
    if (!job.cancelled && job.canStart()) {
      for (let i = cursor; i < cursor + job.concurrency; i++) start(i);
    }
    if (requests[cursor] || job.cancelled) {
      wake?.();
      wake = null;
    }
  };

  for (cursor = 0; cursor < chunks.length; cursor++) {
    job.topUp();
    // 要求を出せない間（先読みが再生中の生成を待つ間）は、出せるようになるまで待つ
    while (!requests[cursor]) {
      if (job.cancelled) return;
      await new Promise<void>((resolve) => {
        wake = resolve;
      });
    }
    let blob: Blob;
    try {
      blob = await (requests[cursor] as Promise<Blob>);
    } catch {
      if (job.cancelled) return;
      // 失敗したチャンク以降は諦め、届いた分までを再生して終える
      job.controller.abort();
      job.failedAt = cursor;
      job.complete = true;
      notifyJob(job);
      return;
    }
    if (job.cancelled) return;
    job.urls.push(URL.createObjectURL(blob));
    notifyJob(job);
  }
  job.complete = true;
  notifyJob(job);
}

export function usePrepareTts({ sessionId, interviewerKey }: UsePrepareTtsOptions = {}): UsePrepareTtsReturn {
  const [audioSrc, setAudioSrc] = useState<string | null>(null);
  const [audioQueue, setAudioQueue] = useState<TtsAudioQueue | null>(null);
  const [customVideoSrc, setCustomVideoSrc] = useState<string | null>(null);
  const [ttsReady, setTtsReady] = useState(false);
  const [ttsLoading, setTtsLoading] = useState(false);
  const [ttsPrepared, setTtsPrepared] = useState(false);
  const [ttsFailed, setTtsFailed] = useState(false);
  const blobUrlRef = useRef<string | null>(null);
  const preparedAudioRef = useRef<string | null>(null);
  const preparedVideoRef = useRef<string | null>(null);
  // 準備の要求の通し番号。最新の要求だけが状態を更新する（#1863）
  const requestSeqRef = useRef(0);
  // 再生中のチャンク生成ジョブとその文面（#1966）。次の準備・離脱で止めて解放する
  const activeJobRef = useRef<{ text: string; job: ChunkJob } | null>(null);
  // 先読み済みのジョブ。文面をキーに引く。1 問先までしか持たない
  const prefetchedJobsRef = useRef<Map<string, ChunkJob>>(new Map());

  const revokePreviousBlobUrl = useCallback(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  }, []);

  const cancelActiveJob = useCallback(() => {
    if (activeJobRef.current) {
      cancelChunkJob(activeJobRef.current.job);
      activeJobRef.current = null;
    }
  }, []);

  const cancelPrefetchedJobs = useCallback(() => {
    prefetchedJobsRef.current.forEach((job) => cancelChunkJob(job));
    prefetchedJobsRef.current.clear();
  }, []);

  // 再生中の生成が無いか。先読みはこの間だけ要求を出し、1 セッションの同時要求を再生の本数に抑える
  const canPrefetch = useCallback(() => {
    const active = activeJobRef.current;
    return active === null || active.job.complete;
  }, []);

  // 再生の生成が終わった（または再生の生成を伴わない準備だった）ときに、待たせていた先読みを進める
  const resumePrefetch = useCallback(() => {
    prefetchedJobsRef.current.forEach((job) => job.topUp());
  }, []);

  // 画面を離れるとき、再生中・先読み済みの Blob URL をすべて解放する
  useEffect(() => {
    return () => {
      cancelActiveJob();
      cancelPrefetchedJobs();
    };
  }, [cancelActiveJob, cancelPrefetchedJobs]);

  const prepareTts = useCallback(async (text: string, questionId?: number | null, { autoPlay = true, generatePath: generatePathOverride }: PrepareTtsOptions = {}) => {
    // 準備の要求に通し番号を付け、最新の要求だけが状態を更新できるようにする（#1863）。
    // 生成に数秒かかる間に面接官を切り替えると、先に始まった旧面接官の要求が後から完了して
    // 新しい面接官の音声を上書きしてしまうため、古い要求の結果は捨てる。
    const seq = ++requestSeqRef.current;
    const isStale = () => seq !== requestSeqRef.current;

    setTtsReady(false);
    setTtsPrepared(false);
    setTtsFailed(false);
    setTtsLoading(true);
    revokePreviousBlobUrl();
    cancelActiveJob();
    setAudioQueue(null);
    preparedAudioRef.current = null;
    preparedVideoRef.current = null;

    // 用意できた音声を、autoPlay の指定に応じて即再生用／再生待ち用にセットする。
    // 古い要求の結果なら反映せず、作った Blob URL だけ解放する
    const applyAudio = (url: string) => {
      if (isStale()) {
        URL.revokeObjectURL(url);
        return;
      }
      blobUrlRef.current = url;
      if (autoPlay) {
        setAudioSrc(url);
      } else {
        preparedAudioRef.current = url;
        setTtsPrepared(true);
      }
    };

    // 音声を用意できなかったとき。案内を出せるよう失敗として返しつつ、
    // テキスト表示のみで練習を続けられる状態にする
    const failToTextOnly = () => {
      if (isStale()) return;
      if (autoPlay) setCustomVideoSrc(null);
      setTtsFailed(true);
      setTtsPrepared(true);
      setTtsReady(true);
    };

    // 面接官ごとの素材の置き場所。識別子をパスに直接連結せず、一覧で解決したディレクトリだけを使う（#1863）。
    const { key: resolvedInterviewerKey, videoDir, audioDir } = resolveInterviewer(interviewerKey);

    try {
      // カスタム動画の存在チェック（ある場合はTTS不要）。
      // 質問別映像は面接官のディレクトリで探す。用意していない面接官では 404 になり、音声の経路へ落ちる。
      if (questionId) {
        try {
          const videoPath = `${videoDir}/${questionId}.mp4`;
          const videoRes = await fetch(videoPath, { method: "HEAD", headers: MEDIA_HEADERS });
          if (isStale()) return;
          if (videoRes.ok) {
            if (autoPlay) {
              setAudioSrc(null);
              setCustomVideoSrc(videoPath);
            } else {
              preparedVideoRef.current = videoPath;
              setTtsPrepared(true);
            }
            return;
          }
        } catch { /* 動画なし、フォールバック */ }
      }

      // カスタム動画なし → クリア
      if (isStale()) return;
      if (autoPlay) setCustomVideoSrc(null);

      // 事前生成済みの静的ファイルを前段キャッシュとして試す（wav → mp3 の順）。
      // 既存質問の読み上げに生成の待ち時間と費用を持ち込まないための経路であり、
      // 静的ファイルが無いことは異常ではない（下の生成経路へ落とす）。
      if (questionId) {
        for (const ext of ["wav", "mp3"]) {
          try {
            const staticRes = await fetch(`${audioDir}/${questionId}.${ext}`, { headers: MEDIA_HEADERS });
            if (isStale()) return;
            if (staticRes.ok) {
              const blob = await staticRes.blob();
              if (isStale()) return;
              applyAudio(URL.createObjectURL(blob));
              return;
            }
          } catch { /* 次のフォーマットを試す */ }
        }
      }

      // 静的ファイルなし → API で生成する。
      // - 面接モード: セッション経由（深掘り質問など、質問IDを持たない発話も読み上げる）
      // - 一問練習: 質問ID 経由（セッションを持たないため #1749 で新設した経路を使う）
      const generatePath =
        generatePathOverride ??
        (sessionId
          ? `/interview_sessions/${sessionId}/tts`
          : questionId
            ? `/questions/${questionId}/tts`
            : null);

      if (!generatePath) {
        // 質問IDもセッションもない → 読み上げる手段が無い
        failToTextOnly();
        return;
      }

      // セッション経由の生成は文単位のチャンクに分けて順に生成し、届いた順に再生する（#1966）。
      // 先読み済みのジョブがあれば引き継ぐ。再生待ち列で流すため即再生のときだけ使い、
      // 準備だけして後で再生する呼び出しは従来どおり全文を 1 回で生成する。
      if (!generatePathOverride && sessionId && autoPlay) {
        let usable: ChunkJob | null = null;
        const prefetched = prefetchedJobsRef.current.get(text);
        if (prefetched) {
          prefetchedJobsRef.current.delete(text);
          // 先読みが失敗していたら捨てて作り直す（失敗した断片を聞かせない）
          if (prefetched.failedAt === null) usable = prefetched;
          else cancelChunkJob(prefetched);
        }
        const chunks = usable?.chunks ?? splitTtsChunks(text);

        if (usable || chunks.length > 1) {
          const job = usable ?? createChunkJob(chunks, PLAYBACK_CONCURRENCY);
          activeJobRef.current = { text, job };
          if (usable) {
            // 先読みは 1 本ずつ・再生の生成を待つ設定で動いている。再生に引き継いだら
            // 再生の本数で残りを先行させ、待ち条件も外す（自身が再生中の生成になるため）
            usable.concurrency = PLAYBACK_CONCURRENCY;
            usable.canStart = () => true;
            usable.topUp();
          } else {
            void runChunkJob(job, generatePath);
          }

          // 届いたチャンクを再生待ち列へ反映する。古い要求・取り消したジョブなら反映しない
          const publish = () => {
            if (isStale() || job.cancelled) return;
            if (job.failedAt === 0) {
              setAudioQueue(null);
              failToTextOnly();
              resumePrefetch();
              return;
            }
            setAudioQueue({ id: seq, urls: [...job.urls], complete: job.complete });
            if (job.urls.length > 0) setTtsLoading(false);
            if (job.failedAt !== null) setTtsFailed(true);
            if (job.complete) resumePrefetch();
          };
          job.listeners.add(publish);
          publish();

          // 最初のチャンクが届く（または失敗・取り消し）まで待って返す。残りは再生の裏で届く
          await new Promise<void>((resolve) => {
            const check = () => {
              if (job.urls.length > 0 || job.complete || job.cancelled) {
                job.listeners.delete(check);
                resolve();
              }
            };
            job.listeners.add(check);
            check();
          });
          return;
        }
      }

      // 質問 ID 経由（一問練習）は、選んだ面接官の識別子を渡してサーバ側で声を解決させる（#1863）。
      // 既定の面接官なら渡さず、従来と同じ要求にする。セッション経由はセッションが面接官を持つ。
      const generateBody = sessionId
        ? { text }
        : resolvedInterviewerKey !== DEFAULT_INTERVIEWER_KEY
          ? { interviewer_key: resolvedInterviewerKey }
          : {};
      const res = await apiClient.post(
        generatePath,
        generateBody,
        { responseType: "blob", timeout: GENERATE_TTS_TIMEOUT_MS },
      );
      if (isStale()) return;
      applyAudio(URL.createObjectURL(res.data));
    } catch {
      // 生成失敗。無音・無エラーで潰さず、失敗として呼び出し側へ返す（#1749）
      failToTextOnly();
    } finally {
      // 古い要求の完了で、新しい要求の読み込み中表示を消さない
      if (!isStale()) setTtsLoading(false);
      // 再生の生成を伴わない準備（静的ファイル・全文 1 回）なら、待たせていた先読みを進める
      if (activeJobRef.current === null) resumePrefetch();
    }
    // 面接官はセッション取得後に決まる。依存に含めることで、決まった後の準備はその面接官の場所で探す
    // （呼び出し元の useCallback が一度作り直されるだけで、副作用は無い）。
  }, [sessionId, interviewerKey, revokePreviousBlobUrl, cancelActiveJob, resumePrefetch]);

  const prefetchTts = useCallback((text: string, questionId?: number | null) => {
    // 質問 ID を持つ固定の設問は事前生成音声を静的ファイルから取るため、生成しない
    if (!sessionId || questionId || !text.trim()) return;
    if (prefetchedJobsRef.current.has(text) || activeJobRef.current?.text === text) return;

    // 1 問先までに限る。古い先読みは捨てて、中断時の無駄な生成を抑える
    cancelPrefetchedJobs();
    const job = createChunkJob(splitTtsChunks(text), PREFETCH_CONCURRENCY, canPrefetch);
    prefetchedJobsRef.current.set(text, job);
    void runChunkJob(job, `/interview_sessions/${sessionId}/tts`);
  }, [sessionId, cancelPrefetchedJobs, canPrefetch]);

  const playTts = useCallback(() => {
    if (!ttsPrepared) return;
    if (preparedVideoRef.current) {
      setAudioSrc(null);
      setCustomVideoSrc(preparedVideoRef.current);
      preparedVideoRef.current = null;
    } else if (preparedAudioRef.current) {
      setCustomVideoSrc(null);
      setAudioSrc(preparedAudioRef.current);
      preparedAudioRef.current = null;
    }
  }, [ttsPrepared]);

  const resetTts = useCallback(() => {
    revokePreviousBlobUrl();
    cancelActiveJob();
    cancelPrefetchedJobs();
    preparedAudioRef.current = null;
    preparedVideoRef.current = null;
    setAudioSrc(null);
    setAudioQueue(null);
    setCustomVideoSrc(null);
    setTtsReady(false);
    setTtsPrepared(false);
    setTtsFailed(false);
    setTtsLoading(false);
  }, [revokePreviousBlobUrl, cancelActiveJob, cancelPrefetchedJobs]);

  const markTtsReady = useCallback(() => setTtsReady(true), []);

  return {
    audioSrc,
    audioQueue,
    customVideoSrc,
    ttsReady,
    ttsLoading,
    ttsPrepared,
    ttsFailed,
    prepareTts,
    prefetchTts,
    playTts,
    resetTts,
    markTtsReady,
  };
}
